import { z } from 'zod';
import { type ScreenId, screenIdSchema } from './registry';
import { screenParamsSchemas } from './params';

type DeeplinkScheme = 'galapagos' | 'https';

type BuildOptions = {
  scheme?: DeeplinkScheme;
  domain?: string;
};

type ParseSuccess = {
  ok: true;
  screenId: ScreenId;
  params: Record<string, string>;
};

type ParseFailure = {
  ok: false;
  reason: 'unknown_screen' | 'invalid_params';
};

export type DeeplinkParseResult = ParseSuccess | ParseFailure;

/**
 * Build a canonical deeplink URL from a screenId and typed params.
 * The screenId IS the URL path — no mapping needed.
 * Validates params against the screen's Zod schema; throws on invalid params.
 */
export function buildDeeplinkUrl<K extends ScreenId>(
  screenId: K,
  params?: Record<string, string>,
  options?: BuildOptions,
): string {
  const scheme = options?.scheme ?? 'galapagos';
  const domain = options?.domain ?? 'app.galapagos.com';

  const schema = screenParamsSchemas[screenId];
  if (params && Object.keys(params).length > 0) {
    // strict() rejects undeclared keys — catch bad params at the call site,
    // not silently drop them from the URL
    schema.strict().parse(params);
  }

  const base = scheme === 'https' ? `https://${domain}/${screenId}` : `${scheme}://${screenId}`;

  if (!params || Object.keys(params).length === 0) {
    return base;
  }

  const searchParams = new URLSearchParams(params);
  return `${base}?${searchParams.toString()}`;
}

// Regex pre-parse for deeplink URLs — avoids `new URL()` divergence between
// Node and Hermes on non-special (custom) schemes.
const CUSTOM_SCHEME_RE = /^([a-z][a-z0-9+.-]*):\/{2}([a-z0-9-/]+)(?:\?(.*))?$/i;

/**
 * Parse a deeplink URL into a screenId and validated params.
 * Returns a discriminated result — the platform decides fallback behaviour.
 * Unknown params are silently stripped.
 */
export function parseDeeplinkUrl(url: string): DeeplinkParseResult {
  let path: string;
  const rawParams: Record<string, string> = {};

  // HTTPS universal links — `URL` handles standard schemes consistently
  if (url.startsWith('https://')) {
    let parsed: URL;
    try {
      parsed = new URL(url);
    } catch {
      return { ok: false, reason: 'unknown_screen' };
    }
    path = parsed.pathname.replace(/^\//, '');
    parsed.searchParams.forEach((value, key) => {
      rawParams[key] = value;
    });
  } else {
    // Custom scheme — regex pre-parse to avoid Node/Hermes `URL` divergence
    const match = url.match(CUSTOM_SCHEME_RE);
    if (!match) {
      return { ok: false, reason: 'unknown_screen' };
    }
    path = match[2];
    if (match[3]) {
      const searchParams = new URLSearchParams(match[3]);
      searchParams.forEach((value, key) => {
        rawParams[key] = value;
      });
    }
  }

  const parseResult = screenIdSchema.safeParse(path);
  if (!parseResult.success) {
    return { ok: false, reason: 'unknown_screen' };
  }

  const screenId = parseResult.data;
  const schema = screenParamsSchemas[screenId];

  // Default Zod mode already strips unknown keys — if this fails,
  // required params are missing/invalid, not an unknown-key issue.
  const paramResult = schema.safeParse(rawParams);
  if (!paramResult.success) {
    return { ok: false, reason: 'invalid_params' };
  }

  const paramsResult = z.record(z.string(), z.string()).safeParse(paramResult.data);
  if (!paramsResult.success) {
    return { ok: false, reason: 'invalid_params' };
  }

  return { ok: true, screenId, params: paramsResult.data };
}

/**
 * Some Android launch paths deliver scheme links in path-style form
 * (`galapagos:///login` — empty host, path-carried destination). The canonical
 * grammar is host-style; callers normalize with this before giving up on a
 * failed parse.
 */
export function normalizePathStyleGalapagosUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'galapagos:' || !parsed.pathname) return null;
    return `galapagos://${parsed.pathname.replace(/^\/+/, '')}${parsed.search}`;
  } catch {
    return null;
  }
}
